import React, { useState } from "react";
import { Users, Calendar, Search, User } from "lucide-react";
import Badge from "../../../components/common/Badge";
import Skeleton from "../../../components/common/Skeleton";
import NoDataFound from "../../../components/common/NoDataFound";
import Pagination from "../../../components/common/Pagination";
import FormControl from "../../../components/common/FormControl";
import { statusVariant } from "../../../utils/statusVariant";

export interface CapaInvestigation {
  id: number;
  issue_code?: string;
  issue_name?: string;
  status: string;
  owner_user_id?: number;
  owner_name?: string | null;
  created_by_name?: string | null;
  due_date?: string | null;
}

interface WorkCapaBodyProps {
  investigations: CapaInvestigation[];
  loading: boolean;
  error: string | null;
  onRowClick: (id: number) => void;
  // History (read-only) twin of the Action Centre. The parent already supplies
  // CLOSED investigations in History mode, so this body does NO list filtering —
  // it just renders whatever it's given. There are no mutating controls in this
  // worklist, so readOnly only affects empty-state copy. Row click still fires.
  readOnly?: boolean;
  // Optional: which worklist scope this body is showing. No-op beyond tweaking
  // the empty-state copy when scope === 'archive'.
  scope?: "active" | "archive";
}

const CardSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="action-card action-card--skeleton">
        <div className="action-card__skel-header">
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <Skeleton.Box width="60%" height={12} />
            <Skeleton.Box width="40%" height={10} />
          </div>
          <Skeleton.Pill width={56} height={20} />
        </div>
        <div className="action-card__skel-meta">
          <Skeleton.Box width={80} height={10} />
          <Skeleton.Box width={100} height={10} />
        </div>
      </div>
    ))}
  </>
);

const PAGE_SIZE = 10;

const WorkCapaBody: React.FC<WorkCapaBodyProps> = ({
  investigations,
  loading,
  onRowClick,
  scope = "active",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const filtered = investigations.filter(
    (inv) =>
      (inv.issue_name ?? "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.issue_code ?? "").toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  if (loading) {
    return (
      <div className="netbody-card flex flex-col h-full">
        <div className="netbody-content">
          <div className="scroll-container-nofilters">
            <div className="action-card-grid">
              <CardSkeleton count={6} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full">
      <div className="netbody-header">
        <div style={{ marginLeft: "auto" }}>
          <FormControl.Input
            data-testid="INP_work_capa_search"
            type="text"
            placeholder="Search investigations…"
            value={searchTerm}
            onChange={handleSearch}
            leadingIcon={<Search size={14} />}
            className="filter-search"
          />
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container">
          {filtered.length === 0 ? (
            <NoDataFound
              title={
                searchTerm
                  ? "No Matching Investigations"
                  : scope === "archive"
                    ? "No Closed Investigations"
                    : "No CAPA Investigations"
              }
              message={
                searchTerm
                  ? "No investigations match your search."
                  : scope === "archive"
                    ? "No closed CAPA investigations to show."
                    : "No CAPA investigations assigned to you."
              }
            />
          ) : (
            <div className="action-card-grid">
              {paginated.map((inv) => (
                <div
                  key={inv.id}
                  data-testid={`BTN_work_capa_row_${inv.id}_open`}
                  className="action-card"
                  onClick={() => onRowClick(inv.id)}
                >
                  <div className="action-card__header">
                    <div className="action-card__title-block">
                      <span className="action-card__title">{inv.issue_name}</span>
                      {inv.issue_code && (
                        <span className="action-card__subtitle">{inv.issue_code}</span>
                      )}
                    </div>
                    <div className="action-card__badge">
                      <Badge variant={statusVariant(inv.status)} size="sm">
                        {inv.status}
                      </Badge>
                    </div>
                  </div>

                  <div className="action-card__meta">
                    {inv.owner_name && (
                      <span className="action-card__meta-item">
                        <Users size={12} />
                        {inv.owner_name}
                      </span>
                    )}
                    {inv.created_by_name && (
                      <span className="action-card__meta-item">
                        <User size={12} />
                        <span style={{ fontWeight: 600 }}>Created by:</span>&nbsp;{inv.created_by_name}
                      </span>
                    )}
                    {inv.due_date && (
                      <span className="action-card__meta-item">
                        <Calendar size={12} />
                        {FormControl.formatDate(inv.due_date)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <Pagination
            page={page}
            pageSize={pageSize}
            totalRecords={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            recordLabel="investigations"
          />
        </div>
      </div>
    </div>
  );
};

export default WorkCapaBody;
